/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box } from "@mui/material"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { MainImageUpload } from "./components/MainImageUpload"
import { saveImage, getImage, ImageKey } from "../../../../services/indexedDBService"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const DescriptionStyle = withMyTheme((theme) => css`
    font-size: 1rem;
    color: ${theme.palette.text.primary};
    text-align: center;
    max-width: 600px;
    line-height: 1.6;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 0.95rem;
        max-width: 90vw;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 50vw;
    
    ${mobileCss(`
        gap: 1.5rem;
        width: 80vw;
    `)}
`)

interface DesktopImageStepProps {
    onStart?: () => void
}

export const DesktopImageStep = ({}: DesktopImageStepProps) => {
    const { t } = useTranslation()
    const { formData, updateMainImages, storageContext, markImageChanged } = useQuestionnaireForm()
    const [imageData, setImageData] = useState<string>('')
    const [loading, setLoading] = useState(true)

    // Load image from IndexedDB on mount
    useEffect(() => {
        const loadImage = async () => {
            if (formData.mainImages.desktopImage) {
                const data = await getImage(ImageKey.DESKTOP_IMAGE, storageContext)
                if (data) {
                    setImageData(data)
                }
            }
            setLoading(false)
        }
        loadImage()
    }, [formData.mainImages.desktopImage, storageContext])

    const handleDesktopImageChange = async (image: string) => {
        try {
            await saveImage(ImageKey.DESKTOP_IMAGE, image, storageContext)
            setImageData(image)
            updateMainImages({ desktopImage: true })
            markImageChanged(ImageKey.DESKTOP_IMAGE)
        } catch (error) {
            console.error('Failed to save desktop image:', error)
        }
    }

    if (loading) {
        return null
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.desktopImage.title')}
            </Typography>
            
            <Typography css={DescriptionStyle}>
                {t('questionnaire.desktopImage.description')}
            </Typography>
            
            <Box css={FormContainerStyle}>
                <MainImageUpload
                    hint={t('questionnaire.desktopImage.hint')}
                    image={imageData}
                    orientation="landscape"
                    minWidth={1728}
                    minHeight={972}
                    onImageChange={handleDesktopImageChange}
                />
            </Box>
        </div>
    )
}
